import { QSingle } from '@educraft/base';
import { IExercise, ExerciseData, addResult } from '../exercise';
import { PdfUnit, PdfRect, PdfBrush, ETextAlign } from '../../pdf/pdf';
import { GUtil } from '../../pdf/gutil';
import { makeCombinedSingle, generateUnique } from './exeUtil';

interface SignInfo {
  supported: boolean;
  leftWidth: number;
  totalWidth: number;
  left: string;
  sign: string;
  right: string;
}

export class ExerciseEqSign implements IExercise {
  readonly name = 'ExerciseEqSign';
  readonly fixedHeight = 0;
  readonly noWindow = false;
  readonly injectEq = true;
  readonly injectAbc = false;

  private marginColumn = PdfUnit.FromMillimeters(8);
  private sizeSign = PdfUnit.FromMillimeters(8);
  private lineHeight = PdfUnit.FromMillimeters(10);
  private resultHeight = PdfUnit.FromMillimeters(8);

  draw(data: ExerciseData): void {
    if (data.item.selEq.length === 0) return;

    const gen = makeCombinedSingle(data);

    let width1 = PdfUnit.FromMillimeters(30);
    let height1 = this.lineHeight;
    const samples: QSingle[] = [];

    for (let i = 0; i < 20; i++) {
      const q = gen.generate(data.data);
      samples.push(q);
      const info = this.parseSign(data, q);
      width1 = Math.max(width1, info.totalWidth + this.marginColumn);
      if (!info.supported) {
        height1 = Math.max(height1, q.texts.length * this.lineHeight);
      }
    }

    const countX = Math.max(1, Math.floor(data.rect.width / (width1 - this.marginColumn)));
    const countY = Math.max(1, Math.floor(data.rect.height / height1));
    const total = countX * countY;

    const array = generateUnique(
      gen,
      data.data,
      total,
      (q) => `${q.texts[0]}|${q.answer}`,
      samples
    );

    const cellW = (data.rect.width + this.marginColumn) / countX;
    const cellH = data.rect.height / countY;

    for (let x = 0; x < countX; x++) {
      for (let y = 0; y < countY; y++) {
        const index = y + x * countY;
        if (index >= array.length) break;
        const item = array[index];
        const r = new PdfRect(
          data.rect.x + x * cellW,
          data.rect.y + y * cellH,
          cellW - this.marginColumn,
          cellH
        );
        const info = this.parseSign(data, item);
        addResult(data, item.texts[0], item.answer);

        if (info.supported) {
          const sizeLeft = data.page.measureString(info.left, GUtil.exerciseFont);
          const sizeRight = data.page.measureString(info.right, GUtil.exerciseFont);

          const rectLeft = new PdfRect(r.x, r.y, sizeLeft.width, this.lineHeight);
          const rectSign = new PdfRect(
            rectLeft.right,
            r.y + (this.lineHeight - this.resultHeight) / 2,
            this.sizeSign,
            this.resultHeight
          );
          const rectRight = new PdfRect(rectSign.right, r.y, sizeRight.width, this.lineHeight);

          data.page.drawString(
            info.left,
            GUtil.exerciseFont,
            PdfBrush.Black,
            null,
            rectLeft,
            ETextAlign.Center
          );
          data.colorSchema.drawResult(data.page, rectSign);
          data.page.drawString(
            info.right,
            GUtil.exerciseFont,
            PdfBrush.Black,
            null,
            rectRight,
            ETextAlign.Center
          );
        } else {
          // Fallback: draw text lines
          for (let i = 0; i < item.texts.length; i++) {
            const rr = new PdfRect(r.x, r.y + i * this.lineHeight, r.width, this.lineHeight);
            data.page.drawString(
              item.texts[i],
              GUtil.exerciseFont,
              PdfBrush.Black,
              null,
              rr,
              ETextAlign.CenterLeft
            );
          }
        }
      }
    }
  }

  private parseSign(data: ExerciseData, q: QSingle): SignInfo {
    const opRegex = /[+\-\u2013*/:\u00d7\u00f7\u2212\u00b7]/;

    if (q.texts.length === 1 && q.texts[0].endsWith('=')) {
      const text = `${q.texts[0]} ${q.answer}`.trim();
      const match = opRegex.exec(text);
      if (match) {
        const left = text.substring(0, match.index);
        const right = text.substring(match.index + match[0].length);

        const sizeLeft = data.page.measureString(left, GUtil.exerciseFont);
        const sizeRight = data.page.measureString(right, GUtil.exerciseFont);

        return {
          supported: true,
          leftWidth: sizeLeft.width,
          totalWidth: sizeLeft.width + sizeRight.width + this.sizeSign,
          left,
          sign: match[0],
          right,
        };
      }
    }

    let width = 0;
    for (const t of q.texts) {
      const sz = data.page.measureString(t, GUtil.exerciseFont);
      width = Math.max(width, sz.width);
    }

    return { supported: false, leftWidth: 0, totalWidth: width, left: '', sign: '', right: '' };
  }
}
